import { Spinner, Table, TableBody, TableCell, TableEmpty, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui";
import { cn } from "@/lib/utils";

export interface Column<T> {
  key: keyof T | string;
  header: string;
  render?: (row: T) => React.ReactNode;
  /** 数值列: 右对齐 + 等宽数字 */
  numeric?: boolean;
  className?: string;
}

interface Props<T> {
  rows: T[] | undefined;
  columns: Column<T>[];
  loading?: boolean;
  empty?: string;
  /** 合计行：按列 key 给出单元格内容；没给的列留空，第一列缺省显示「合计」 */
  totals?: Partial<Record<string, React.ReactNode>>;
}

/** 默认单元格格式：数字千分位 (toLocaleString)，空值显示 "-" */
function formatCell(v: unknown): string {
  if (v === null || v === undefined || v === "") return "-";
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}

export function StatsTable<T extends object>({
  rows,
  columns,
  loading,
  empty = "暂无数据",
  totals,
}: Props<T>) {
  const waiting = loading && !rows;
  const isEmpty = !rows || rows.length === 0;

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          {columns.map((c) => (
            <TableHead key={String(c.key)} numeric={c.numeric}>
              {c.header}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {waiting ? (
          <tr>
            <td colSpan={columns.length} className="py-10 text-center text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <Spinner /> 加载中…
              </span>
            </td>
          </tr>
        ) : isEmpty ? (
          <TableEmpty colSpan={columns.length} title={empty} />
        ) : (
          rows.map((r, i) => (
            <TableRow key={i}>
              {columns.map((c) => (
                <TableCell key={String(c.key)} numeric={c.numeric} className={cn(!c.numeric && "whitespace-nowrap", c.className)}>
                  {c.render ? c.render(r) : formatCell((r as Record<string, unknown>)[c.key as string])}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
      {totals && !waiting && !isEmpty && (
        <TableFooter>
          <TableRow className="hover:bg-transparent">
            {columns.map((c, i) => {
              const v = totals[String(c.key)];
              return (
                <TableCell key={String(c.key)} numeric={c.numeric} className={c.className}>
                  {v !== undefined ? (typeof v === "number" ? v.toLocaleString() : v) : i === 0 ? "合计" : ""}
                </TableCell>
              );
            })}
          </TableRow>
        </TableFooter>
      )}
    </Table>
  );
}
